/**
 * 시급 변경 이력 서비스
 * 직원의 시급 변경 이력을 관리합니다.
 */

import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  addDoc,
  Timestamp,
  onSnapshot,
  QuerySnapshot,
} from 'firebase/firestore';
import { db } from './firebaseService';
import { HourlyRateHistory, HourlyRateUpdateType } from '../types/hourlyRateHistory';

class HourlyRateHistoryService {
  /**
   * 컬렉션 경로 가져오기
   */
  private getCollectionPath(userId: string) {
    return `sharedBooks/${userId}/hourly_rate_history`;
  }

  /**
   * Firestore 데이터를 모델로 변환
   */
  private _fromFirestore(id: string, data: any): HourlyRateHistory {
    // Timestamp를 Date로 변환하는 헬퍼 함수
    const toDate = (timestamp: any): Date => {
      if (!timestamp) return new Date();
      if (timestamp instanceof Date) return timestamp;
      if (timestamp && typeof timestamp.toDate === 'function') {
        return timestamp.toDate();
      }
      if (typeof timestamp === 'string' || typeof timestamp === 'number') {
        return new Date(timestamp);
      }
      return new Date();
    };

    return {
      id,
      employeeId: data.employee_id,
      previousRate: data.previous_rate,
      newRate: data.new_rate,
      changedAt: toDate(data.changed_at),
      accountNumber: data.account_number,
      reason: data.reason || undefined,
      updateType: data.update_type === 'fullUpdate' 
        ? HourlyRateUpdateType.FULL_UPDATE 
        : HourlyRateUpdateType.NEW_ONLY,
      createdAt: toDate(data.created_at),
      updatedAt: toDate(data.updated_at),
    };
  }

  /**
   * 특정 직원의 시급 변경 이력 조회
   */
  async getHistoryByEmployee(
    userId: string,
    employeeId: string,
    accountNumber: number
  ): Promise<HourlyRateHistory[]> {
    try {
      
      const collectionRef = collection(db, 'sharedBooks', userId, 'hourly_rate_history');
      
      // Flutter와 동일하게: employee_id로만 필터링하고 클라이언트에서 account_number 필터링 및 정렬
      // 이렇게 하면 인덱스가 필요 없음
      const q = query(
        collectionRef,
        where('employee_id', '==', employeeId)
      );

      const snapshot = await getDocs(q);
      
      let histories = snapshot.docs.map((doc) => {
        const data = doc.data();
        return this._fromFirestore(doc.id, data);
      });
      
      // 클라이언트에서 account_number 필터링 및 정렬
      histories = histories
        .filter(h => h.accountNumber === accountNumber)
        .sort((a, b) => b.changedAt.getTime() - a.changedAt.getTime());
      
      return histories;
    } catch (error: any) {
      console.error('❌ HourlyRateHistoryService.getHistoryByEmployee 오류:', error);
      console.error('❌ 오류 상세:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      return [];
    }
  }

  /**
   * 모든 시급 변경 이력 조회 (동기화용)
   */
  async getAllHistory(
    userId: string,
    accountNumber: number
  ): Promise<HourlyRateHistory[]> {
    try {
      
      const collectionRef = collection(db, 'sharedBooks', userId, 'hourly_rate_history');
      
      // 모든 데이터를 가져온 후 클라이언트에서 필터링 및 정렬 (인덱스 불필요)
      const snapshot = await getDocs(collectionRef);
      
      let histories = snapshot.docs.map((doc) => this._fromFirestore(doc.id, doc.data()));
      
      // 클라이언트에서 account_number 필터링 및 정렬
      histories = histories
        .filter(h => h.accountNumber === accountNumber)
        .sort((a, b) => b.changedAt.getTime() - a.changedAt.getTime());
      
      return histories;
    } catch (error) {
      console.error('❌ HourlyRateHistoryService.getAllHistory 오류:', error);
      return [];
    }
  }

  /**
   * 시급 변경 이력 저장
   */
  async saveHistory(
    userId: string,
    history: Omit<HourlyRateHistory, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<string> {
    try {
      
      const now = Timestamp.now();
      const historyData = {
        employee_id: history.employeeId,
        previous_rate: history.previousRate,
        new_rate: history.newRate,
        changed_at: Timestamp.fromDate(history.changedAt),
        account_number: history.accountNumber,
        reason: history.reason || null,
        update_type: history.updateType === HourlyRateUpdateType.FULL_UPDATE ? 'fullUpdate' : 'newOnly',
        created_at: now,
        updated_at: now,
      };

      const collectionRef = collection(db, 'sharedBooks', userId, 'hourly_rate_history');
      
      const docRef = await addDoc(collectionRef, historyData);

      return docRef.id;
    } catch (error: any) {
      console.error('❌ HourlyRateHistoryService.saveHistory 오류:', error);
      console.error('❌ 오류 상세:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      throw error;
    }
  }

  /**
   * 특정 날짜의 유효한 시급 조회
   */
  async getEffectiveRateForDate(
    userId: string,
    employeeId: string,
    accountNumber: number,
    targetDate: Date
  ): Promise<number | null> {
    try {
      const histories = await this.getHistoryByEmployee(userId, employeeId, accountNumber);
      
      // fullUpdate 타입 중 가장 최근 것 찾기
      const fullUpdate = histories.find(h => h.updateType === HourlyRateUpdateType.FULL_UPDATE);
      if (fullUpdate && fullUpdate.changedAt <= targetDate) {
        return fullUpdate.newRate;
      }

      // newOnly 타입 중 targetDate 이전의 가장 최근 것 찾기
      const newOnly = histories
        .filter(h => h.updateType === HourlyRateUpdateType.NEW_ONLY && h.changedAt <= targetDate)
        .sort((a, b) => b.changedAt.getTime() - a.changedAt.getTime())[0];

      return newOnly?.newRate || null;
    } catch (error) {
      console.error('❌ HourlyRateHistoryService.getEffectiveRateForDate 오류:', error);
      return null;
    }
  }

  /**
   * 실시간 구독
   */
  subscribeHistory(
    userId: string,
    employeeId: string,
    accountNumber: number,
    callback: (histories: HourlyRateHistory[]) => void
  ): () => void {
    const collectionRef = collection(db, 'sharedBooks', userId, 'hourly_rate_history');
    // employee_id로만 필터링 (인덱스 불필요)
    const q = query(
      collectionRef,
      where('employee_id', '==', employeeId)
    );

    return onSnapshot(q, (snapshot: QuerySnapshot) => {
      let histories = snapshot.docs.map((doc) =>
        this._fromFirestore(doc.id, doc.data())
      );
      
      // 클라이언트에서 account_number 필터링 및 정렬
      histories = histories
        .filter(h => h.accountNumber === accountNumber)
        .sort((a, b) => b.changedAt.getTime() - a.changedAt.getTime());
      
      callback(histories);
    });
  }
}

export const hourlyRateHistoryService = new HourlyRateHistoryService();

